import Decimal from 'decimal.js';
import { useCallback, useState } from 'react';
import Web3Singleton from 'web3Singleton';

import { getActiveNetworkOverride } from 'constants/runtime-network';

import { StakingContractType } from 'enums/staking-contract-type.enum';

import { getStakingContractAddressByType } from 'utils/get-staking-contract-address-by-type.utils';

import { EarningsTableStatus, UserDeposit, UserStakingTotals } from '../types';
import { delay, RPC_DELAY_MS, mapPeriodicalDeposits, mapWhitelistedDeposits, mapVipDeposits } from '../utils';

interface UseUserDepositsProps {
  walletAddress: string | null;
  tokenDecimals: number;
  hasCurrency: boolean;
}

interface UseUserDepositsReturn {
  deposits: UserDeposit[];
  totals: UserStakingTotals;
  fetchDeposits: () => Promise<void>;
  fetchPeriodicalTotalStaked: () => Promise<void>;
  fetchWhitelistedPoolData: () => Promise<void>;
  fetchPeriodicalRewardsAndClaimable: () => Promise<void>;
  resetUserData: () => void;
}

const initialTotals: UserStakingTotals = {
  periodicalTotalStaked: '0',
  periodicalRewardsExpected: '0',
  whitelistedTotalStaked: '0',
  whitelistedTotalClaimableInterest: '0',
  periodicalClaimableStaked: '0',
  periodicalClaimableRewards: '0',
  periodicalClaimableCount: 0,
  whitelistedClaimableCount: 0,
  // VIP
  vipTotalStaked: '0',
  vipRewardsExpected: '0',
  vipClaimableStaked: '0',
  vipClaimableRewards: '0',
};

/**
 * Hook for fetching user deposits and staking totals.
 * Handles both periodical and whitelisted staking contracts.
 */
export const useUserDeposits = ({
  walletAddress,
  tokenDecimals,
  hasCurrency,
}: UseUserDepositsProps): UseUserDepositsReturn => {
  const lgoWeb3 = Web3Singleton.getInstance();

  const [deposits, setDeposits] = useState<UserDeposit[]>([]);
  const [totals, setTotals] = useState<UserStakingTotals>(initialTotals);

  /**
   * Resets all user data (used when wallet disconnects)
   */
  const resetUserData = useCallback(() => {
    setDeposits([]);
    setTotals(initialTotals);
  }, []);

  /**
   * Fetches user's total staked amount from periodical staking
   */
  const fetchPeriodicalTotalStaked = useCallback(async () => {
    if (!walletAddress) return;

    try {
      // UserDataType.STAKING = 0
      const staked = await lgoWeb3.periodicalStakingGetUserData(0, walletAddress);
      setTotals((prev) => ({ ...prev, periodicalTotalStaked: staked }));
    } catch (e) {
      console.error('Failed to fetch periodical total staked:', e);
    }
  }, [lgoWeb3, walletAddress]);

  /**
   * Fetches whitelisted staking pool data (Games Pool staking totals)
   */
  const fetchWhitelistedPoolData = useCallback(async () => {
    if (!walletAddress) return;

    try {
      const whitelistedData = await lgoWeb3.checkPoolUserInfo(walletAddress);

      if (whitelistedData) {
        setTotals((prev) => ({
          ...prev,
          whitelistedTotalStaked: whitelistedData.totalStakedAmount.toString(),
          whitelistedTotalClaimableInterest: whitelistedData.totalClaimableInterest.toString(),
        }));
      }
    } catch (e) {
      console.error('Failed to fetch whitelisted pool data:', e);
    }
  }, [lgoWeb3, walletAddress]);

  /**
   * Fetches user's expected rewards and claimable data from periodical staking
   */
  const fetchPeriodicalRewardsAndClaimable = useCallback(async () => {
    if (!walletAddress) return;

    try {
      // UserDataType.REWARD_EXPECTED = 3
      const periodicalRewards = await lgoWeb3.periodicalStakingGetUserData(3, walletAddress);
      const claimable = await lgoWeb3.periodicalStakingCheckClaimableDataFor(walletAddress);

      const stakedClaimable = claimable[0];
      const periodicalReward = claimable[1];
      const indefiniteReward = claimable[2];
      const totalRewards = new Decimal(periodicalReward).add(indefiniteReward).toString();

      setTotals((prev) => ({
        ...prev,
        periodicalRewardsExpected: new Decimal(periodicalRewards).add(indefiniteReward).toString(),
        periodicalClaimableStaked: stakedClaimable,
        periodicalClaimableRewards: totalRewards,
      }));
    } catch (e) {
      console.error('Failed to fetch periodical rewards:', e);
    }
  }, [lgoWeb3, walletAddress]);

  /**
   * Fetches user's deposits (periodical + whitelisted + VIP)
   */
  const fetchDeposits = useCallback(async () => {
    if (!walletAddress || !hasCurrency) return;

    try {
      // Fetch periodical deposits
      const depositCount = await lgoWeb3.periodicalStakingDepositCount(walletAddress);
      let periodicalDeposits: UserDeposit[] = [];

      if (depositCount > 0) {
        await delay(RPC_DELAY_MS);
        const results = await lgoWeb3.getPeriodicalStakingDepositsInRange(
          walletAddress,
          0,
          depositCount
        );
        periodicalDeposits = mapPeriodicalDeposits(results, tokenDecimals);
      }

      // Fetch VIP deposits
      await delay(RPC_DELAY_MS);
      const vipDepositCount = await lgoWeb3.vipStakingDepositCount(walletAddress);
      let vipDeposits: UserDeposit[] = [];

      const activeNetworkId =
        getActiveNetworkOverride()?.networkId ?? process.env.REACT_APP_NETWORK_ID ?? null;
      const activeVipContractAddress = getStakingContractAddressByType(StakingContractType.VIP) ?? null;

      if (vipDepositCount > 0) {
        await delay(RPC_DELAY_MS);
        const vipResults = await lgoWeb3.getVipStakingDepositsInRange(
          walletAddress,
          0,
          vipDepositCount
        );
        vipDeposits = mapVipDeposits(
          vipResults,
          periodicalDeposits.length,
          tokenDecimals,
          walletAddress,
          activeNetworkId,
          activeVipContractAddress
        );
      }

      // Fetch whitelisted deposits
      await delay(RPC_DELAY_MS);
      const whitelistedDepositCount = await lgoWeb3.whitelistedStakingDepositCount(walletAddress);
      let whitelistedDeposits: UserDeposit[] = [];

      if (whitelistedDepositCount > 0) {
        await delay(RPC_DELAY_MS);
        const whitelistedResults = await lgoWeb3.getWhitelistedDepositsInRange(
          walletAddress,
          0,
          whitelistedDepositCount
        );
        whitelistedDeposits = mapWhitelistedDeposits(
          whitelistedResults,
          periodicalDeposits.length + vipDeposits.length,
          tokenDecimals
        );
      }

      // Merge and set deposits
      setDeposits([...periodicalDeposits, ...vipDeposits, ...whitelistedDeposits]);

      // Calculate VIP totals from deposits
      const activeVipDeposits = vipDeposits.filter(
        (d) => !d.withdrawalDate && d.status !== EarningsTableStatus.CLAIMED
      );
      const vipTotalStaked = activeVipDeposits.reduce(
        (sum, d) => new Decimal(sum).add(d.amountRaw).toString(),
        '0'
      );
      const vipRewardsExpected = activeVipDeposits.reduce(
        (sum, d) => new Decimal(sum).add(d.rewardsRaw).toString(),
        '0'
      );
      const claimableVipDeposits = vipDeposits.filter((d) => d.canClaim || d.canWithdraw);
      const vipClaimableStaked = claimableVipDeposits.reduce(
        (sum, d) => d.canWithdraw ? new Decimal(sum).add(d.amountRaw).toString() : sum,
        '0'
      );
      const vipClaimableRewards = claimableVipDeposits.reduce(
        (sum, d) => d.canClaim ? new Decimal(sum).add(d.rewardsRaw).toString() : sum,
        '0'
      );

      // Update claimable counts and VIP totals
      setTotals((prev) => ({
        ...prev,
        periodicalClaimableCount: periodicalDeposits.filter((d) => d.canClaim).length,
        whitelistedClaimableCount: whitelistedDeposits.filter((d) => d.canClaim).length,
        vipTotalStaked,
        vipRewardsExpected,
        vipClaimableStaked,
        vipClaimableRewards,
      }));
    } catch (e) {
      console.error('Failed to fetch deposits:', e);
    }
  }, [lgoWeb3, walletAddress, hasCurrency, tokenDecimals]);

  return {
    deposits,
    totals,
    fetchDeposits,
    fetchPeriodicalTotalStaked,
    fetchWhitelistedPoolData,
    fetchPeriodicalRewardsAndClaimable,
    resetUserData,
  };
};
